import { computed, defineComponent, onMounted, onUnmounted, ref, watch, type PropType } from 'vue'
import { RouterLink } from 'vue-router'
import {
  Sticky as VanSticky,
  Tabs as VanTabs,
  Tab as VanTab,
  List as VanList,
  Icon as VanIcon,
} from 'vant'
import { useRegionStore } from '@/stores/region-store'
import {
  useHomeGalleryStore,
  useFindGalleryStore,
  useGszjGalleryStore,
} from '@/stores/gallery-feed-store'
import { getApiV1TukuPictureSeriesByRegion } from '@/api/sdk.gen'
import type { PictureSeries, RecommendItem } from '@/api/types.gen'
import LotterySwitcher from '@/components/lottery-switcher'

type GalleryColor = 'color' | 'bw'

const RECENT_YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020]

const COLORS: Array<{ value: GalleryColor; label: string }> = [
  { value: 'color', label: '彩色' },
  { value: 'bw', label: '黑白' },
]

/** iconText 小组件：图标 + 文字（入口按钮常用结构） */
/** 瀑布流按奇偶分到左右两列（第 0、2、4… 项进左列），每列带固定的 key */
export function splitWaterfall<T>(list: T[]): { key: 'left' | 'right'; items: T[] }[] {
  const left: T[] = []
  const right: T[] = []
  list.forEach((item, i) => {
    ;(i % 2 === 0 ? left : right).push(item)
  })
  return [
    { key: 'left', items: left },
    { key: 'right', items: right },
  ]
}

/** 图库分类 / 搜索页的色彩下拉菜单：彩色(1) / 黑白(2) */
export function renderGalleryColorMenu(active: 1 | 2, onSelect: (c: 1 | 2) => void) {
  return (
    <ul class="down-ul">
      {/* 样式挂在 `.down-ul li` 上：li 保留，按钮放里面撑满 */}
      <li style="border-bottom:1px solid #ddd" class={{ act: active === 1 }}>
        <button type="button" class="btn-block" onClick={() => onSelect(1)}>
          彩色
        </button>
      </li>
      <li class={{ act: active === 2 }}>
        <button type="button" class="btn-block" onClick={() => onSelect(2)}>
          黑白
        </button>
      </li>
    </ul>
  )
}

export const IconText = defineComponent({
  props: { text: String },
  setup(props, { slots }) {
    return () => (
      <div class="icon-text">
        {slots.default?.()}
        <p class="text">{props.text}</p>
      </div>
    )
  },
})

/**
 * 图库瀑布流区：sticky 年份栏 + 系列栏 + 双列瀑布流。
 * 原为首页内部组件（HomeBodyImage），抽出供底部「图库」Tab 页复用。
 * 依赖 `.home .body-image` 系列样式（49208.css），调用方根元素需带 `home` class。
 */
/** 按入口取对应的图库列表 store（各入口分页状态互不影响） */
function pickGalleryStore(key: 'home' | 'find' | 'gszj') {
  if (key === 'find') return useFindGalleryStore()
  if (key === 'gszj') return useGszjGalleryStore()
  return useHomeGalleryStore()
}

export const GalleryWaterfall = defineComponent({
  props: {
    /** 各页面用独立 store 实例，分页状态互不干扰 */
    storeKey: { type: String as PropType<'home' | 'find' | 'gszj'>, default: 'home' },
    /** sticky 吸顶偏移：有固定 NavBar 的页面传 46，无则 0 */
    stickyOffsetTop: { type: Number, default: 46 },
    /** 年份栏右上角显示港彩/澳彩切换（图库 Tab 页无首页的开奖区切换，需自带入口，#250） */
    showRegionToggle: { type: Boolean, default: false },
  },
  setup(props) {
    const store = pickGalleryStore(props.storeKey)
    const regionStore = useRegionStore()
    const series = ref<PictureSeries[]>([])
    const sentinelRef = ref<HTMLDivElement>()
    let observer: IntersectionObserver | null = null

    async function fetchSeries() {
      const res = await getApiV1TukuPictureSeriesByRegion({
        path: { region: regionStore.region },
      })
      series.value = res.data?.data ?? []
    }

    onMounted(() => {
      fetchSeries()
      store.loadMoreGallery(regionStore.region)
      if (sentinelRef.value) {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting) {
              store.loadMoreGallery(regionStore.region)
            }
          },
          { rootMargin: '200px' },
        )
        observer.observe(sentinelRef.value)
      }
    })

    onUnmounted(() => observer?.disconnect())

    // 切换港彩/澳彩时刷新系列并重载瀑布流（store 内按地区自动清空旧列表；
    // 组件未挂载期间在别处切换的地区，由 onMounted 的 loadMoreGallery 同样兜住）
    watch(
      () => regionStore.region,
      (region) => {
        fetchSeries()
        store.loadMoreGallery(region)
      },
    )

    // 双列瀑布流
    const columns = computed(() => splitWaterfall(store.gallery))

    /** 年份 × 色彩 tab：标题图标点进图库搜索 */
    function renderYearColorTab(year: number, { value: color, label }: (typeof COLORS)[number]) {
      return (
        <VanTab key={`${year}-${color}`} name={`${year}-${color}`}>
          {{
            title: () => (
              <IconText text={`${year}年${label}`}>
                <RouterLink
                  class={['icon-bg btn-reset', color === 'bw' ? 'gray' : '']}
                  to={`/gallery/search?year=${year}&color=${color}&region=${regionStore.region}`}
                  aria-label={`${year}年${label}`}
                >
                  <VanIcon name="photo" color="#fff" />
                </RouterLink>
              </IconText>
            ),
          }}
        </VanTab>
      )
    }

    /** 瀑布流单张卡片 */
    function renderCard(item: RecommendItem, i: number) {
      return (
        <RouterLink
          key={`${item.pictureTypeId}-${i}`}
          class="btn-reset block rounded-[6Px] overflow-hidden bg-white shadow-[0_1px_5px_0_rgba(160,160,160,0.5)]"
          to={`/gallery/${item.pictureTypeId}?region=${regionStore.region}`}
        >
          {item.pictureUrl && (
            <img
              src={item.pictureUrl}
              alt={item.pictureName}
              class="w-full block"
              loading="lazy"
              referrerpolicy="no-referrer"
            />
          )}
          <div class="image-item-title">{item.pictureName}</div>
        </RouterLink>
      )
    }

    return () => {
      const displaySeries = series.value.slice(0, 3)
      return (
        <div class="body-image">
          <VanSticky offsetTop={props.stickyOffsetTop}>
            <div class="body-image-hd">
              {/* 图库 Tab 页：年份栏上方单独一行，右侧放资料大全同款圆形「港/澳」切换 */}
              {props.showRegionToggle && (
                <div class="flex items-center justify-end pb-[0.16rem]">
                  <LotterySwitcher />
                </div>
              )}
              <VanTabs lineWidth="0" shrink>
                {RECENT_YEARS.flatMap((year) => COLORS.map((c) => renderYearColorTab(year, c)))}
              </VanTabs>
            </div>
            <div class="body-xl">
              {displaySeries.map((s) => (
                <RouterLink
                  key={s.id}
                  class="item btn-reset"
                  to={`/gallery/category/${s.id}?region=${regionStore.region}`}
                >
                  {s.name}
                </RouterLink>
              ))}
              {Array.from({ length: Math.max(0, 3 - displaySeries.length) }).map((_, i) => (
                <div key={`no-${i}`} class="item no" />
              ))}
              <RouterLink class="more btn-reset" to={`/gallery/categories?region=${regionStore.region}`}>
                更多
              </RouterLink>
            </div>
          </VanSticky>
          <VanList
            immediateCheck={false}
            loading={store.galleryLoading}
            finished={!store.galleryHasMore}
            finishedText="没有更多了"
            onLoad={() => store.loadMoreGallery(regionStore.region)}
          >
            <div class="body-image-bd">
              <div class="flex gap-[8Px] px-[8Px]">
                {columns.value.map((col) => (
                  <div key={col.key} class="flex-1 flex flex-col gap-[8Px]">
                    {col.items.map((item, i) => renderCard(item, i))}
                  </div>
                ))}
              </div>
              <div ref={sentinelRef} class="h-[40Px]" />
            </div>
          </VanList>
        </div>
      )
    }
  },
})

export default GalleryWaterfall
